export const DAILY_PUZZLE_RETURN_KEY = "playmath:daily-puzzle-return";
export const DAILY_PUZZLE_RETURN_PATH = "/daily";
const DAILY_PUZZLE_RETURN_TTL_MS = 30 * 60 * 1000;

interface DailyPuzzleReturnPayload {
  slug: string;
  ts: number;
}

function parseReturnPayload(raw: string | null): DailyPuzzleReturnPayload | null {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const slug = "slug" in parsed && typeof parsed.slug === "string" ? parsed.slug.trim() : "";
    const ts = "ts" in parsed && typeof parsed.ts === "number" ? parsed.ts : Number.NaN;
    if (!slug || !Number.isFinite(ts)) return null;
    return { slug, ts };
  } catch {
    return null;
  }
}

/** Remember that this game was opened from a Daily Puzzle picker so close can return to /daily. */
export function markDailyPuzzleReturn(slug: string): void {
  try {
    if (typeof window === "undefined") return;
    const payload: DailyPuzzleReturnPayload = { slug: slug.trim(), ts: Date.now() };
    if (!payload.slug) return;
    sessionStorage.setItem(DAILY_PUZZLE_RETURN_KEY, JSON.stringify(payload));
  } catch (error) {
    console.error("Error marking daily puzzle return", { slug, error });
  }
}

/**
 * True when close should send the player back to /daily for this slug.
 * Consumes the flag so a later unrelated game close does not bounce to Daily Puzzle.
 */
export function consumeDailyPuzzleReturn(slug: string): boolean {
  try {
    if (typeof window === "undefined") return false;
    const payload = parseReturnPayload(sessionStorage.getItem(DAILY_PUZZLE_RETURN_KEY));
    if (!payload) return false;
    if (payload.slug !== slug.trim()) return false;
    if (Date.now() - payload.ts > DAILY_PUZZLE_RETURN_TTL_MS) {
      sessionStorage.removeItem(DAILY_PUZZLE_RETURN_KEY);
      return false;
    }
    sessionStorage.removeItem(DAILY_PUZZLE_RETURN_KEY);
    return true;
  } catch (error) {
    console.error("Error consuming daily puzzle return", { slug, error });
    return false;
  }
}

const pendingLeaveClearTimeouts = new Map<string, number>();

export function cancelPendingDailyPuzzleReturnClear(slug: string): void {
  const key = slug.trim();
  if (typeof window === "undefined" || !key) return;
  const timeoutId = pendingLeaveClearTimeouts.get(key);
  if (timeoutId === undefined) return;
  window.clearTimeout(timeoutId);
  pendingLeaveClearTimeouts.delete(key);
}

/**
 * Clear a matching return marker after this game visit actually ends.
 * The timeout is delayed so React Strict Mode remounts can cancel it and keep Close → /daily working.
 */
export function scheduleDailyPuzzleReturnClearOnLeave(slug: string): () => void {
  const key = slug.trim();
  if (typeof window === "undefined" || !key) return () => undefined;

  cancelPendingDailyPuzzleReturnClear(key);

  return () => {
    const timeoutId = window.setTimeout(() => {
      pendingLeaveClearTimeouts.delete(key);
      consumeDailyPuzzleReturn(key);
    }, 0);
    pendingLeaveClearTimeouts.set(key, timeoutId);
  };
}
